import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import { AppLayout } from '@/components/layout/AppLayout';
import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DealsPage } from '@/pages/DealsPage';
import { DealDetailPage } from '@/pages/DealDetailPage';
import { SubmitDealPage } from '@/pages/SubmitDealPage';
import { MineralsPage } from '@/pages/MineralsPage';
import { MineralDetailPage } from '@/pages/MineralDetailPage';
import { MarketsPage } from '@/pages/MarketsPage';
import { MatchingPage } from '@/pages/MatchingPage';
import { DataRoomPage } from '@/pages/DataRoomPage';
import { CRMPage } from '@/pages/CRMPage';
import { CommissionsPage } from '@/pages/CommissionsPage';
import { AccessRequestsPage } from '@/pages/AccessRequestsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { PipelinePage } from '@/pages/PipelinePage';
import { AdminPage } from '@/pages/AdminPage';
import { InvestmentPreferencesPage } from '@/pages/InvestmentPreferencesPage';
import { AIMatchingPage } from '@/pages/AIMatchingPage';
import { DigitalAssetPage } from '@/pages/DigitalAssetPage';
import { ViewAnalyticsPage } from '@/pages/ViewAnalyticsPage';
import { RegionalMapPage } from '@/pages/RegionalMapPage';
import { SendMatchPage } from '@/pages/SendMatchPage';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/deals" element={<ProtectedRoute><DealsPage /></ProtectedRoute>} />
      <Route path="/deals/submit" element={<ProtectedRoute><SubmitDealPage /></ProtectedRoute>} />
      <Route path="/deals/:id" element={<ProtectedRoute><DealDetailPage /></ProtectedRoute>} />
      <Route path="/minerals" element={<ProtectedRoute><MineralsPage /></ProtectedRoute>} />
      <Route path="/minerals/:id" element={<ProtectedRoute><MineralDetailPage /></ProtectedRoute>} />
      <Route path="/markets" element={<ProtectedRoute><MarketsPage /></ProtectedRoute>} />
      <Route path="/matching" element={<ProtectedRoute><MatchingPage /></ProtectedRoute>} />
      <Route path="/data-room" element={<ProtectedRoute><DataRoomPage /></ProtectedRoute>} />
      <Route path="/crm" element={<ProtectedRoute><CRMPage /></ProtectedRoute>} />
      <Route path="/commissions" element={<ProtectedRoute><CommissionsPage /></ProtectedRoute>} />
      <Route path="/access-requests" element={<ProtectedRoute><AccessRequestsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><PipelinePage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/preferences" element={<ProtectedRoute><InvestmentPreferencesPage /></ProtectedRoute>} />
      <Route path="/ai-matching" element={<ProtectedRoute><AIMatchingPage /></ProtectedRoute>} />
      <Route path="/digital-assets" element={<ProtectedRoute><DigitalAssetPage /></ProtectedRoute>} />
      <Route path="/view-analytics" element={<ProtectedRoute><ViewAnalyticsPage /></ProtectedRoute>} />
      <Route path="/regional-map" element={<ProtectedRoute><RegionalMapPage /></ProtectedRoute>} />
      <Route path="/send-match" element={<ProtectedRoute><SendMatchPage /></ProtectedRoute>} />
      
      <Route path="*" element={user ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
